<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import FlowWidget from '@/widgets/Flow/FlowWidget/FlowWidget.vue'
  import FlowCardSkeleton from '@/shared/components/rebranding/FlowCard/FlowCardSkeleton.vue'
  import { PolicyDownloadFormWidget } from '@/widgets/Flow/PolicyDownloadFormWidget'
  import { PolicyDownloadListWidget } from '@/widgets/Flow/PolicyDownloadListWidget'
  import { useRecaptcha } from '@qicgroupdoha/qic-use'
  import {
    ERROR_TOO_MANY_REQUETS,
    ERROR_NO_POLICY_FOUND,
    usePolicyStore,
    useCoreStore,
  } from '@/shared/store'
  import { BaseError } from '@/shared/components/rebranding/BaseError'
  import {
    STATE_POLICY_DOWNLOAD_KEY,
    STATE_POLICY_DOWNLOAD_VALUE,
  } from '@/shared/constants/other.constants'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { useRoute } from 'vue-router'

  const { initRecaptcha, executeCaptcha } = useRecaptcha()

  const policyStore = usePolicyStore()
  const coreStore = useCoreStore()
  const homeUrl = useHomeUrl()
  const route = useRoute()
  const abortController = new AbortController()

  const isLoading = ref(false)
  const isLoadingButton = ref(false)
  const isError = ref(false)
  const errorMessage = ref('')
  const errorType =
    ref<Nullable<typeof ERROR_TOO_MANY_REQUETS | typeof ERROR_NO_POLICY_FOUND>>(null)
  const stateView = ref<Nullable<string>>(null)

  const person = computed(() => ({
    passport: policyStore.person.passportNo,
    birthdate: policyStore.person.birthdate,
  }))

  const setStateView = (view: string) => {
    stateView.value = view
    localStorage.setItem(STATE_POLICY_DOWNLOAD_KEY, stateView.value)
  }

  const getPolicyNumber = (): string => {
    const policyno = route.query.policyno || ''

    if (Array.isArray(policyno)) {
      return policyno[0] || ''
    }

    return policyno
  }

  const policyNumber = getPolicyNumber()

  const fetchPolicyList = async (passport: string, birthdate: Date) => {
    if (!passport || !birthdate) {
      return setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD)
    }

    try {
      await initRecaptcha(import.meta.env.VUE_APP_RECAPTCHA_SITE_KEY)
      const captchaToken = await executeCaptcha('download_policy')
      await policyStore.fetchPolicyList(passport, birthdate, captchaToken, abortController)
      if (policyStore.policyList.length) {
        setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD_LIST)
      }
    } catch (error: any) {
      if (error?.response?.status === 400 && !error?.response.data.data?.length) {
        errorType.value = ERROR_NO_POLICY_FOUND
        setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD)
        return console.error(error)
      }
      if (error?.message === ERROR_TOO_MANY_REQUETS) {
        errorType.value = ERROR_TOO_MANY_REQUETS
        setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD)
        return console.error(error)
      }

      isError.value = true
      errorMessage.value = error.message
    }
  }

  const onProceed = async (passport: string, birthdate: Date) => {
    isLoadingButton.value = true
    policyStore.setPerson(passport, birthdate)
    await fetchPolicyList(passport, new Date(birthdate))
    isLoadingButton.value = false
  }

  const handleBack = () => {
    if (stateView.value && stateView.value === STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD_LIST) {
      setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD)
    } else {
      window.location.href = homeUrl
    }
  }

  const resolveError = () => {
    isError.value = false
    errorMessage.value = ''
  }

  onMounted(async () => {
    isLoading.value = true
    coreStore.getContacts()
    stateView.value = localStorage.getItem(STATE_POLICY_DOWNLOAD_KEY) ?? null
    if (
      stateView.value === STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD_LIST &&
      person.value.passport &&
      person.value.birthdate
    ) {
      await fetchPolicyList(person.value.passport, person.value.birthdate)
    } else {
      setStateView(STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD)
    }
    isLoading.value = false
  })

  onUnmounted(() => {
    abortController.abort()
  })
</script>

<template>
  <FlowWidget :with-arrow-back="false" disable-progress-bar>
    <Transition name="fade" mode="out-in">
      <template v-if="!isError && !isLoading">
        <Transition name="fade" mode="out-in">
          <PolicyDownloadFormWidget
            v-if="stateView === STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD"
            v-model:error-type="errorType"
            :passport="person.passport"
            :birthdate="person.birthdate"
            :is-loading="isLoadingButton"
            :policy-number="policyNumber"
            data-test="policy-download-form"
            @back="handleBack"
            @proceed="onProceed"
          />
          <PolicyDownloadListWidget
            v-else-if="
              stateView === STATE_POLICY_DOWNLOAD_VALUE.POLICY_DOWNLOAD_LIST &&
              policyStore.policyList.length
            "
            :passport="person.passport"
            :birthdate="person.birthdate"
            :policy-list="policyStore.policyList"
            :home-url="homeUrl"
            @back="handleBack"
          />
        </Transition>
      </template>

      <FlowCardSkeleton v-else-if="!isError && isLoading" />

      <BaseError v-else-if="isError" :message="errorMessage" @reload="resolveError" />
    </Transition>
  </FlowWidget>
</template>

<style lang="scss" scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.15s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
